import { useCallback } from "react";
import { IChatWidget } from "@cma/chat-widgets/types/ChatWidget";
import { useChatWidgetStore } from "@cma/store/chat-widget.store";
import { ChatMessageArtifact } from "@store/messages.store";
import { useUIStore } from "@store/ui.store";

const DEFAULT_WIDGET_VALUES = {
  "widget-listings-search": {
    kind: "widget-listings-search" as const,
    title: "Search Results",
  },
  "widget-cma-details": {
    kind: "widget-cma-details" as const,
    title: "CMA Presentation",
  },
  "widget-listing-details": {
    kind: "widget-listing-details" as const,
    title: "Property Details",
  },
  "widget-error": {
    kind: "widget-error" as const,
    title: "Error",
  },
};

export const useChatWidgetLogic = () => {
  const {
    setChatWidget: setChatWidgetInner,
    resetChatWidget,
    chatWidget,
    chatWidgetDrawerState,
    toggleChatWidgetDrawer,
    markChatWidgetStale,
    setChatWidgetContext,
    temporaryWidgetThreadId,
    linkTemporaryWidgetThread,
  } = useChatWidgetStore((state) => state);
  const toggleSidebar = useUIStore((state) => state.toggleSidebar);

  const convertArtifactToWidget = useCallback(
    (artifact: ChatMessageArtifact): IChatWidget | null => {
      switch (artifact.kind) {
        case "widget-listings-search":
          return {
            ...DEFAULT_WIDGET_VALUES["widget-listings-search"],
            artifactId: artifact.id,
            content: artifact.content.arguments,
          };
        case "widget-cma-details":
          return {
            ...DEFAULT_WIDGET_VALUES["widget-cma-details"],
            artifactId: artifact.id,
            content: artifact.content,
          };
        case "widget-listing-details": {
          return {
            ...DEFAULT_WIDGET_VALUES["widget-listing-details"],
            artifactId: artifact.id,
            listingReference: {
              mlsId: artifact.content.listingReference.mlsId,
              mlsSource: artifact.content.listingReference.mlsSource,
            },
          };
        }
        default:
          console.warn(
            `Unsupported widget kind: ${artifact.kind} for artifact: ${artifact.id}`,
          );
          return null;
      }
    },
    [],
  );

  const setArtifactAsChatWidget = useCallback(
    (artifact: ChatMessageArtifact) => {
      const widget = convertArtifactToWidget(artifact);

      if (!widget) return;

      setChatWidgetInner(widget);
    },
    [convertArtifactToWidget, setChatWidgetInner],
  );

  const setChatWidget = useCallback(
    (widget: IChatWidget) => {
      setChatWidgetInner({
        ...widget,
        title: widget.title ?? DEFAULT_WIDGET_VALUES[widget.kind]?.title,
      });
    },
    [setChatWidgetInner],
  );

  return {
    setChatWidget,
    setArtifactAsChatWidget,
    resetChatWidget,
    toggleChatWidgetDrawer,
    markChatWidgetStale,
    setChatWidgetContext,
    toggleSidebar,
    chatWidget,
    chatWidgetDrawerState,
    temporaryWidgetThreadId,
    linkTemporaryWidgetThread,
  };
};
